import {action, computed, observable, override, makeObservable} from 'mobx';
import {DesignField} from '../../../stores/DesignField';
import {TreeDesignCollection} from './TreeDesignCollection';
import * as fieldTypes from '../../../../../../constants/FieldTypes';
import {hiddenFieldTypes} from '../../../../../../constants/FieldConsts';

export class TreeDesignField extends DesignField {
    @observable newarea: boolean;
    @observable merge: boolean; // has an attached merged field
    @observable mergedField: TreeDesignField;
    @observable merged: TreeDesignField; // is merged with previous field
    general: boolean;

    constructor(field, parentField) {
        super(field, parentField);
        makeObservable(this);
        this.type = field.type;
        this.general = field.general;
        this.newarea = field.newarea;
        this.merge = field.merge;
        this.collection = new TreeDesignCollection(parentField.collection);
    }

    setCollection(field) {
        field.fields && this.collection.insertFields(this, field.fields);
    }

    toggleHide() {
        super.toggleHide();
        // newarea is reseted when we hide a field
        if (this.hidden && this.newarea) {
            this.setNewArea(false);
        }
        // if we hide or show a tab, all fields' hidden status changes accordingly
        if (this.type === fieldTypes.TAB) {
            this.collection.fields.forEach(f => {
                f.setHide(this.hidden);
            });
        }
        // else it's a field in a tab
        else {
            // if we hide a field
            if (this.hidden) {
                //and it's the last visible field in the tab, mark the tab as hidden
                if (this.parentCollection.firstHiddenIdx === 0) {
                    this.parentField.setHide(hiddenFieldTypes.HIDDEN);
                    this.parentField.parentCollection.moveField(
                        this.parentField,
                        this.parentField.parentCollection.firstHiddenIdx
                    );
                }
            }
            // if we show a field
            else {
                //and its parent is a hidden tab, mark the tab as shown and move up
                if (this.parentField.hidden) {
                    this.parentField.setHide(null);
                    this.parentField.parentCollection.moveField(
                        this.parentField,
                        this.parentField.parentCollection.firstHiddenIdx
                    );
                }
            }
        }
    }

    @override setHide(value) {
        this.hiddenStatus = value;
        if (this.mergedField) {
            this.mergedField.setHide(value);
        }
    }

    @action toggleMerge() {
        this.merge = !this.merge;
    }

    @action setMergedField(field) {
        if (field) {
            field.merged = this;
            this.mergedField = field;
        } else {
            this.mergedField = null;
        }
    }

    @action setNewArea(value) {
        this.newarea = value;
    }

    @override setGrouped(value) {
        this.grouped = value;
        if (this.mergedField) {
            this.mergedField.setGrouped(value);
        }
    }

    @override cancelTitleChange() {
        if (this.type === fieldTypes.TAB && !this.title) {
            this.parentCollection.deleteField(this);
            this.screen.setActive(null);
        } else {
            this.setChanging(false);
        }
    }

    @override get isSelected() {
        const {selected} = this.screen;
        return (
            selected &&
            (selected.name === this.name || (this.merged && selected.name === this.merged.name))
        );
    }

    @computed get isTabEmpty() {
        return this.type === fieldTypes.TAB && this.collection.isEmpty;
    }

    @override get needed() {
        if (this.type !== fieldTypes.TAB) {
            return this.fieldNeeded;
        } else {
            // if it's a tab, it's needed when there're needed fields inside it
            return this.collection.fields.findIndex(field => field.needed === true) >= 0;
        }
    }

    @override
    get isMovable() {
        return !this.hidden && !this.general;
    }

    @override
    get isSelectable() {
        return !this.isChanging && !this.merged && (!this.hidden || this.type === fieldTypes.TAB);
    }

    @override get isDoubleTitleAllowed() {
        if (this.type === fieldTypes.TAB) {
            return false;
        } else {
            return true;
        }
    }

    @override
    get isHiddenStatusUnchangable() {
        return (
            this.merged ||
            //we don't allow hide in the follwoing case (while "show" could be an option)
            (!this.hidden &&
                (this.needed ||
                    /* we don't allow to hide a field when it's merged field is "needed" */
                    (this.mergedField && this.mergedField.needed) ||
                    /* we don't allow to hide the last field/tab in a screen */
                    (this.parentField.type !== fieldTypes.TAB &&
                        this.index === 0 &&
                        (!this.nextField || this.nextField.hidden))))
        );
    }

    get sameLevelField() {
        if (this.type === fieldTypes.TAB) {
            return this.parentCollection.fields;
        } else {
            return this.screen.collection.lastLevelFields;
        }
    }

    setMovedDown(value) {
        if (this.mergedField) {
            this.mergedField.movedDown = value;
        } else {
            this.movedDown = value;
        }
    }

    isDouble(field, newTitle) {
        return (
            field.title.toLowerCase() === newTitle.toLowerCase() ||
            (field.mergedField && field.mergedField.title.toLowerCase() === newTitle.toLowerCase())
        );
    }
}
